import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';

import { colour, pillSx } from '../admin/tokens';
import { formatMoney } from '../../utils/money';

/**
 * Where this family is on their re-enrolment, what they chose on the way, and what the payment
 * page was asking them for.
 *
 * The list's two columns answer "who do I ring first" across hundreds of families. This answers
 * "what do I say" once one of them is open: the step they reached, so the conversation can start
 * where they left off, and the figure they were looking at, which for a family who stopped at the
 * payment page is usually the reason they stopped.
 *
 * The server decides the stage (backend utils/journeyStage.js). The ladder of names below is
 * presentation, and is checked against the server's own count: if the two ever disagree — a step
 * added on the server and not here — the ladder is dropped and the server's label for the step
 * they reached is shown on its own, rather than ticking off the wrong rungs.
 */

/** The journey as a parent walks it. Must stay in step with JOURNEY in the backend's journeyStage.js. */
export const LADDER = [
    'Signed in',
    'Filled in their details',
    'Answered the discount questions',
    'Saw the fees and chose what to pay',
    'Entered a card',
    'Finished',
];

const Rung = ({ name, done, current }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Box sx={{
            width: 18,
            height: 18,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: done ? colour.primary : colour.panel,
            border: `1px solid ${done ? colour.primary : colour.border}`,
        }}>
            {done && <CheckIcon sx={{ fontSize: 13, color: '#fff' }} />}
        </Box>
        <Typography sx={{
            fontSize: '13px',
            // The step they are standing on is the one being discussed, so it is the one that is
            // legible at a glance; the ones behind it are history and the ones ahead have not
            // happened.
            fontWeight: current ? 700 : 500,
            color: current ? colour.ink : (done ? colour.body : colour.faint),
        }}>
            {name}
        </Typography>
    </Box>
);

/** One "they chose X" line. Nothing is drawn for a question they were never asked. */
const Chose = ({ label, value }) => (value === null || value === undefined || value === '' ? null : (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '12px', color: colour.muted, minWidth: 110 }}>{label}</Typography>
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colour.body }}>{value}</Typography>
    </Box>
));

const yesNo = (value) => (value === true ? 'Yes' : (value === false ? 'No' : null));

const JourneyPanel = ({ journey }) => {
    if (!journey) return null;

    const laddered = journey.of === LADDER.length;
    const chose = journey.chose || {};
    const amounts = journey.amounts;
    const ours = journey.waitingOn === 'us';

    return (
        <Box sx={{
            bgcolor: colour.surface,
            border: `1px solid ${colour.border}`,
            borderRadius: '12px',
            p: { xs: 1.5, sm: 2 },
            mb: 2.5,
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: colour.ink }}>
                    Where they are
                </Typography>
                {journey.holding && (
                    <Chip size="small" label={ours ? 'Waiting on us' : 'Waiting on them'} sx={pillSx(ours ? 'warn' : 'neutral')} />
                )}
            </Box>

            {/* Two columns from a tablet up: the ladder on the left, what they chose on the right.
                One under the other on a phone, ladder first. */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 1.5, sm: 2.5 } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
                    {laddered
                        ? LADDER.map((name, index) => (
                            <Rung
                                key={name}
                                name={name}
                                done={index < journey.step}
                                current={index === journey.step - 1}
                            />
                        ))
                        : <Rung name={journey.label} done current />}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
                    <Chose label="Children" value={chose.children} />
                    <Chose label="Discount" value={chose.discount} />
                    <Chose label="Staff member" value={yesNo(chose.staffMember)} />
                    <Chose label="Health care card" value={yesNo(chose.healthCareCard)} />
                    <Chose label="Pay rest by" value={chose.payRestBy} />

                    {/* What the payment page would have asked for. Only reaches a team that may
                        see amounts; for anybody else the server leaves it off the row. */}
                    {amounts && (
                        <Box sx={{ mt: 0.75, pt: 0.75, borderTop: `1px solid ${colour.border}` }}>
                            <Chose label="Asked for now" value={formatMoney(amounts.dueNow)} />
                            {amounts.debt > 0 && (
                                <Chose label="Owed from before" value={formatMoney(amounts.debt)} />
                            )}
                            <Chose label="Over the year" value={formatMoney(amounts.overTheYear)} />
                        </Box>
                    )}
                </Box>
            </Box>

            {journey.holding && (
                <Typography sx={{ fontSize: '13px', color: colour.body, mt: 1.5, pt: 1.25, borderTop: `1px solid ${colour.border}` }}>
                    <strong>Holding:</strong>
                    {` ${journey.holding}`}
                </Typography>
            )}
        </Box>
    );
};

export default JourneyPanel;
